import { z } from "zod";
export const ErrorCodeSchema = z.enum([
    "conflict",
    "entitlement_read_only",
    "forbidden",
    "idempotency_mismatch",
    "internal",
    "job_enqueue_failed",
    "not_found",
    "not_a_member",
    "org_header_missing",
    "rate_limited",
    "revision_conflict",
    "unauthenticated",
    "validation_failed",
]);
export const ErrorDetailSchema = z.strictObject({
    field: z.string(),
    message: z.string(),
});
export const ErrorEnvelopeSchema = z.strictObject({
    error: z.strictObject({
        code: ErrorCodeSchema,
        message: z.string(),
        field: z.string().optional(),
        request_id: z.string(),
        details: z.array(ErrorDetailSchema).optional(),
    }),
});
export const HealthResponseSchema = z.strictObject({
    status: z.literal("ok"),
    service: z.literal("safeplate-api"),
});
export const MobileClientSchema = z.enum(["ios", "android"]);
export const MembershipRoleSchema = z.enum(["admin", "manager", "staff"]);
export const IdempotencyKeySchema = z.uuid();
export const MeResponseSchema = z.strictObject({
    session: z.strictObject({
        auth_method: z.enum(["primary", "shared_pin"]),
        scope_org_id: z.uuid().nullable(),
        shared_device_id: z.uuid().nullable(),
    }),
    user: z.strictObject({
        id: z.uuid(),
        email: z.email(),
        name: z.string(),
        image: z.string().nullable(),
    }),
    memberships: z.array(z.strictObject({
        org_id: z.uuid(),
        org_name: z.string(),
        role: MembershipRoleSchema,
        location_id: z.uuid().nullable(),
    })),
    staff_profiles: z.array(z.strictObject({
        org_id: z.uuid(),
        staff_profile_id: z.uuid(),
    })),
});
export const PostalAddressSchema = z.strictObject({
    line1: z.string().optional(),
    line2: z.string().optional(),
    suburb: z.string().optional(),
    city: z.string().optional(),
    postcode: z.string().optional(),
});
const organisationMutableFields = {
    legal_name: z.string().refine((value) => value.trim().length > 0, {
        message: "Legal name is required",
    }),
    trading_name: z.string().nullable(),
    nzbn: z.string().nullable(),
    billing_email: z.email().nullable(),
    phone: z.string().nullable(),
    postal_address: PostalAddressSchema.nullable(),
};
export const OrganisationResponseSchema = z.strictObject({
    id: z.uuid(),
    ...organisationMutableFields,
});
export const OrganisationCreateRequestSchema = z.strictObject({
    legal_name: organisationMutableFields.legal_name,
    trading_name: organisationMutableFields.trading_name.optional(),
    nzbn: organisationMutableFields.nzbn.optional(),
    billing_email: organisationMutableFields.billing_email.optional(),
    phone: organisationMutableFields.phone.optional(),
    postal_address: organisationMutableFields.postal_address.optional(),
});
export const OrganisationUpdateRequestSchema = z
    .strictObject({
    legal_name: organisationMutableFields.legal_name.optional(),
    trading_name: organisationMutableFields.trading_name.optional(),
    nzbn: organisationMutableFields.nzbn.optional(),
    billing_email: organisationMutableFields.billing_email.optional(),
    phone: organisationMutableFields.phone.optional(),
    postal_address: organisationMutableFields.postal_address.optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one organisation field is required",
});
export function isIanaTimeZone(value) {
    try {
        new Intl.DateTimeFormat("en-NZ", { timeZone: value }).format();
        return true;
    }
    catch {
        return false;
    }
}
export const IanaTimeZoneSchema = z.string().refine(isIanaTimeZone, {
    message: "Timezone must be a valid IANA timezone",
});
export const LocationStatusSchema = z.enum(["setup", "active", "archived"]);
export const EntitlementStateSchema = z.enum([
    "none",
    "trialing",
    "active",
    "past_due",
    "grace",
    "read_only",
    "cancelled",
]);
const locationMutableFields = {
    name: z.string().refine((value) => value.trim().length > 0, {
        message: "Location name is required",
    }),
    trading_name: z.string().nullable(),
    address: PostalAddressSchema,
    council_area: z.string().nullable(),
    timezone: IanaTimeZoneSchema,
    phone: z.string().nullable(),
    email: z.email().nullable(),
    licensable: z.boolean(),
    status: LocationStatusSchema,
};
export const LocationResponseSchema = z.strictObject({
    id: z.uuid(),
    ...locationMutableFields,
    entitlement_state: EntitlementStateSchema,
});
export const LocationCreateRequestSchema = z.strictObject({
    name: locationMutableFields.name,
    trading_name: locationMutableFields.trading_name.optional(),
    address: locationMutableFields.address.default({}),
    council_area: locationMutableFields.council_area.optional(),
    timezone: locationMutableFields.timezone.default("Pacific/Auckland"),
    phone: locationMutableFields.phone.optional(),
    email: locationMutableFields.email.optional(),
    licensable: locationMutableFields.licensable.default(true),
    status: z.enum(["setup", "active"]).default("active"),
});
export const LocationUpdateRequestSchema = z
    .strictObject({
    name: locationMutableFields.name.optional(),
    trading_name: locationMutableFields.trading_name.optional(),
    address: locationMutableFields.address.optional(),
    council_area: locationMutableFields.council_area.optional(),
    timezone: locationMutableFields.timezone.optional(),
    phone: locationMutableFields.phone.optional(),
    email: locationMutableFields.email.optional(),
    licensable: locationMutableFields.licensable.optional(),
    status: locationMutableFields.status.optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one location field is required",
});
export const LocationIdParamsSchema = z.strictObject({ id: z.uuid() });
export const SocialProviderSchema = z.enum(["google", "apple"]);
export const SocialSignInRequestSchema = z.strictObject({
    provider: SocialProviderSchema,
    id_token: z.string().min(1).optional(),
});
export const RefreshSessionRequestSchema = z.strictObject({
    refresh_token: z.string().min(32),
});
export const MobileTokenPairSchema = z.strictObject({
    access_token: z.string(),
    expires_in: z.number().int().positive(),
    refresh_expires_in: z.number().int().positive(),
    refresh_token: z.string(),
    token_type: z.literal("Bearer"),
});
